import React, { ReactNode, PropsWithChildren, useContext } from "react";
import { Flex, Box, Text, Divider, Center } from "@chakra-ui/react";
import Slider from "react-slick";
import _values from "lodash/values";
import dynamic from "next/dynamic";

// Constants
import { CLICK_DEFI_EXPLORE } from "constants/analytics";
import { defiOptions, slickSettings } from "./constants";

// Components
import DefiCard from "./DefiCard";

// Types
import { DefiData } from "types/common";

// Utils
import { getNumbersFromValue } from "utils/common";

// Contexts
import { StakeContext } from "providers/StakeProvider";

const Link = dynamic(() => import("components/Link"), {
  ssr: false,
});

const SliderFixed = Slider as unknown as React.FC<
  PropsWithChildren<{ children: ReactNode }>
>;

const DefiPreview = ({
  staderTVL,
  defiData,
}: {
  staderTVL: string;
  defiData: DefiData;
}) => {
  const { track } = useContext(StakeContext);

  const getMaxAPY = () => {
    const apys = _values(defiData)
      .filter(({ isSDPool }) => !isSDPool)
      .map(({ apy }) => getNumbersFromValue(apy));

    return Math.ceil(Math.max(...apys));
  };

  return (
    <Box
      bg="brand.backgroundLight"
      boxShadow="0px 4px 12px rgba(53, 56, 140, 0.05)"
      borderRadius="8px"
      p="1.5rem"
    >
      <Center flexDir={{ base: "column", lg: "row" }}>
        <Text
          as="span"
          fontSize="16px"
          color="brand.textInactive"
          textAlign="center"
          mr={{ base: 0, lg: "1.5rem" }}
          fontFamily="regular"
        >
          Stader TVL
        </Text>
        <Text
          as="span"
          fontFamily="semi-bold"
          fontSize="1.25rem"
          lineHeight="1.5rem"
          height="1.5rem"
        >
          {staderTVL ? `$ ${staderTVL}` : "N/A"}
        </Text>
      </Center>
      <Divider borderColor="brand.separator" my="1.5rem" />
      <Flex
        align="center"
        justifyContent="space-between"
        mb="2rem"
        fontSize={{ base: "14px", md: "1rem" }}
        gap="0.5rem"
      >
        <Box>
          <Text fontFamily="medium">Get upto {getMaxAPY()}% rewards</Text>
          <Text color="brand.textInactive">Explore Defi Opportunities</Text>
        </Box>
        <Link
          link="/liquid-staking/defi"
          minW="120px"
          borderRadius="0.5rem"
          bg="#EFE6FF"
          color="#7630EC"
          border="1px solid #7630EC"
          cursor="pointer"
          p={{ base: "0.5rem 1rem", md: "0.5rem 2rem" }}
          fontSize="14px"
          _hover={{
            color: "#7630EC",
          }}
          _dark={{
            bg: "#7630EC",
            color: "white",
          }}
          onClick={() => track(CLICK_DEFI_EXPLORE)}
        >
          Explore DeFi
        </Link>
      </Flex>
      <Box mb="1.5rem">
        <SliderFixed {...slickSettings}>
          {defiOptions.map((option) => (
            <DefiCard
              key={option.id}
              id={option.id}
              icon={option.icon}
              tokenIcon={option.tokenIcon}
              token={option.token}
              partnerName={option.partnerName}
              link={option.link}
              hasAPYBoost={defiData[option.id]?.apyBoost !== undefined}
              apy={
                defiData[option.id] &&
                (defiData[option.id].apyBoost
                  ? (
                      Number(defiData[option.id].apy) +
                      +(defiData[option.id].apyBoost || 0)
                    ).toFixed(2)
                  : defiData[option.id].apy)
              }
              orgAPY={
                defiData[option.id] &&
                Number(defiData[option.id].apy).toFixed(2)
              }
              tvl={defiData[option.id]?.tvl}
              showTVL={option.showTvl}
              highlight={defiData[option.id].highlight || ""}
            />
          ))}
        </SliderFixed>
      </Box>
    </Box>
  );
};

export default DefiPreview;
